"use client";

import Link from "next/link";
import type { SDKLanguage } from "../../lib/sdk";

interface SdkSidebarProps {
  language: SDKLanguage;
  lang: string;
  /** Set on class detail pages to highlight the active class */
  activeClassId?: string;
  /** Explicitly set the active/expanded category (required on class detail pages) */
  activeCatId?: string;
  /** "index" = category-only nav; "class" = active category shows siblings */
  mode?: "index" | "class";
}

const MAX_SIBLINGS = 12;

export default function SdkSidebar({
  language,
  lang,
  activeClassId,
  activeCatId,
  mode = "index",
}: SdkSidebarProps) {
  return (
    <aside className="hidden lg:flex flex-col w-60 xl:w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 sticky top-0 h-screen overflow-hidden">
      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-200 flex-shrink-0">
        <Link
          href="/sdk"
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-3"
        >
          ← SDK Reference
        </Link>
        <div className="flex items-center gap-2">
          <Link
            href={`/sdk/${lang}`}
            className="text-sm font-semibold text-gray-700 hover:text-gray-900"
          >
            {language.name}
          </Link>
          <span className="text-xs text-gray-400 font-mono">{language.version}</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {language.categories.map((cat) => {
          const classes = cat.class_ids
            .map((id) => language.classes[id])
            .filter(Boolean);
          if (classes.length === 0) return null;

          const isActiveCategory = cat.id === activeCatId;
          const showSiblings = mode === "class" && isActiveCategory;

          return (
            <div key={cat.id}>
              {/* Category row — links to the category page */}
              <Link
                href={`/sdk/${lang}/${cat.id}`}
                className={`flex items-center justify-between w-full px-2.5 py-2 rounded-md text-left transition-colors ${
                  isActiveCategory
                    ? "bg-white shadow-sm border border-gray-200 text-gray-900"
                    : "text-gray-500 hover:text-gray-700 hover:bg-white"
                }`}
              >
                <span className="text-xs font-semibold uppercase tracking-wider">
                  {cat.name}
                </span>
                <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                  {classes.length}
                </span>
              </Link>

              {/* Siblings — only shown on class detail page for active category */}
              {showSiblings && (
                <ul className="mt-0.5 mb-1 ml-1 space-y-0.5 pl-2 border-l border-gray-200">
                  {cat.class_ids.slice(0, MAX_SIBLINGS).map((classId) => {
                    const cls = language.classes[classId];
                    if (!cls) return null;
                    const isActive = classId === activeClassId;
                    return (
                      <li key={classId}>
                        <Link
                          href={`/sdk/${lang}/${cat.id}/${classId}`}
                          className={`block px-2 py-1 rounded text-xs font-mono transition-all ${
                            isActive
                              ? "text-green-700 font-semibold"
                              : "text-gray-500 hover:text-gray-900"
                          }`}
                        >
                          {isActive && <span className="mr-1 text-green-500">›</span>}
                          {cls.name}
                        </Link>
                      </li>
                    );
                  })}
                  {cat.class_ids.length > MAX_SIBLINGS && (
                    <li>
                      <Link
                        href={`/sdk/${lang}/${cat.id}`}
                        className="block px-2 py-1 text-xs text-gray-400 hover:text-green-600"
                      >
                        +{cat.class_ids.length - MAX_SIBLINGS} more →
                      </Link>
                    </li>
                  )}
                </ul>
              )}
            </div>
          );
        })}

        {/* FQN Browser */}
        <div className="pt-3 mt-2 border-t border-gray-200">
          <Link
            href="/sdk/fqn"
            className="flex items-center justify-between px-2.5 py-2 rounded-md text-xs text-gray-500 hover:text-gray-900 hover:bg-white transition-all"
          >
            <span>FQN Browser</span>
            <span className="text-gray-400">↗</span>
          </Link>
        </div>
      </nav>
    </aside>
  );
}
